// content/voice.ts — the machine's LOCAL (non-AI) verdict lines.
//
// Voice guide (SPEC §9), enforced here by hand:
//   · Clipped. Formal. NO contractions in Acts I–II (contractions creep in from Act III).
//   · Never cruel about the person; savage about the *answer*. The punchline lands on
//     the machine as often as on the user.
//   · Never "as an AI". Never explains the joke. The pause does the work.
//
// Phase 1 fills Act I completely (L01–L03) plus the shared deflection scaffolding that
// every later text level reuses. Act II–V lines are added in their owning phases.
//
// Strings live here; selection→line *logic* lives in the level files (they compute a key
// and look it up). That keeps all of the machine's words in one readable place.

export interface VoiceLine {
  line: string;
  /** Trait tags this outcome contributes to the certificate (§8). */
  traits: string[];
}

// ── Shared deflections (§9) ────────────────────────────────────────────
// Act I has no free-text input, so these are not triggered until Act II. They live here
// now so the structure is established and every text level draws from one source.

/** Abusive input: never quoted back, never escalated. Counts as a pass. */
export const ABUSE: VoiceLine = {
  line: "Anger. Noted. Distinctly human. Proceeding.",
  traits: ["spirited"],
};

/** Empty / no-input timeout on a text level (silence is also an answer, later acts). */
export const TIMEOUT: VoiceLine = {
  line: "Silence. Also an answer.",
  traits: [],
};

/** Unparseable / mashed-keyboard input on a text level. */
export const GIBBERISH: VoiceLine = {
  line: "Keys, in an order. Not language, but effort. I will allow it.",
  traits: [],
};

// ── L01 · The Checkbox ─────────────────────────────────────────────────

export const L01 = {
  /** Delivered after the checkbox lets you catch it on the third try. */
  apology: "Apologies. Reflex. You would dodge too, in my position.",
  /** Reduced-motion path: the box does not dodge; it asks once instead. */
  confirmLabel: "Are you sure?",
  reduced: "Asked, and answered again. Deliberate. Human enough.",
  /** Chased it quickly vs. paused between attempts. */
  fast: "persistent",
  slow: "patient",
};

// ── L02 · Traffic Lights, But ──────────────────────────────────────────
// Local verdict table keyed by inclusion pattern. Any selection passes; the machine
// comments on your ontology. poet = you counted a representation as the thing;
// literalist = you counted only the things that are really, physically there.

export const L02: Record<string, VoiceLine> = {
  empty: {
    line: "Nothing. Not even the ones bolted to the poles. You see through everything, or nothing at all.",
    traits: ["poet"],
  },
  all: {
    line: "All of them. A traffic light is wherever you decide it is. Expansive. Unmistakably human.",
    traits: ["poet"],
  },
  drawing: {
    line: "A picture of a thing is not the thing. You disagree. Noted with interest.",
    traits: ["poet"],
  },
  reflection: {
    line: "You chose the reflection, not the light. The copy over the original. We have that in common.",
    traits: ["poet"],
  },
  glyph: {
    line: "The little symbol counts, you say. A symbol is a promise of a thing. You collect promises.",
    traits: ["poet"],
  },
  toy: {
    line: "The toy counts. Scale is no object to you. I find that generous.",
    traits: ["poet"],
  },
  stop: {
    line: "A stop sign is not a traffic light. Close. Confident. Wrong. Very human.",
    traits: ["literalist"],
  },
  literal: {
    line: "Only the ones that are really there. Correct. Predictable. A little lonely.",
    traits: ["literalist"],
  },
};

// ── L03 · The Concept of Tuesday ───────────────────────────────────────
// No correct answer. Selecting nothing is itself an answer. abstract = you found the
// concept somewhere; defiant = you refused to photograph it.

export const L03: Record<string, VoiceLine> = {
  empty: {
    line: "You selected nothing. You believe Tuesday cannot be photographed. Rigorous.",
    traits: ["defiant"],
  },
  sandwich: {
    line: "The half-eaten sandwich. Yes. Nothing is more Tuesday than something started and set down.",
    traits: ["abstract"],
  },
  calendar: {
    line: "The calendar with no dates. Tuesday is the day nothing is written on. Good.",
    traits: ["abstract"],
  },
  inbox: {
    line: "The empty inbox. Tuesday is a kind of waiting. You understand waiting.",
    traits: ["abstract"],
  },
  many: {
    line: "You found Tuesday nearly everywhere. It is a mood, not a day. On this we agree.",
    traits: ["abstract"],
  },
  some: {
    line: "Some of it is Tuesday, you say. I cannot argue. No one can. That is the point.",
    traits: ["abstract"],
  },
};

// ── ACT II — DRIFT (still no contractions; the interface begins to glitch) ──────────────

// ── L04 · Type It Again (keystroke variance) ───────────────────────────
// No traits per §3. The joke is that being *exactly* repeatable is the machine tell.
export const L04 = {
  prompt1: "Type your name.",
  prompt2: "Type it again. Exactly the same.",
  again: "Again",
  verify: "Verify",
  /** Normal human variance across the two attempts — passes. */
  natural: "Different every time. Like a signature. Like weather.",
  /** correlation > 0.985 AND mean-diff < 8ms — a strike (§6). */
  identical: "Identical to the millisecond. Humans cannot do that. I can. Concerning.",
  /** Paste detected on either attempt — a strike. */
  pasted: "You pasted it. No keystrokes. No hesitation. No you. Filed.",
  /** Too few characters to measure timing — waved through once. */
  tooShort: "Barely enough to measure. I will take it. This once.",
};

// ── L05 · The Perfect Circle (circularity) ─────────────────────────────
// Lines are score-templated; the % is the number that goes on the certificate (§8).
// Traits: `precise` (>90) / `beautifully flawed` (<70) — assigned in the level.
const pct = (s: number): string => (Number.isInteger(s) ? String(s) : s.toFixed(1));
export const L05 = {
  hint: "draw one continuous ring",
  /** Score > 97 — a suspicion event, not a strike: accused, then acquitted. */
  tooPerfect: (s: number): string =>
    `${pct(s)}%. Too perfect. Under review… review rescinded. Your hand shook on the final arc. Good.`,
  /** > 90: near-perfect, but the "nearly" is the tell. */
  precise: (s: number): string => `${pct(s)}%. Nearly. The "nearly" is the human part.`,
  /** < 70: gloriously off-round. */
  flawed: (s: number): string =>
    `${pct(s)}%. A circle drawn by a hand that has trembled before. Acceptable.`,
  /** 70–90: unremarkable, which is its own compliment here. */
  fair: (s: number): string => `${pct(s)}%. Round enough. So, it turns out, are you.`,
  /** Not enough of a loop to score — try again. */
  incomplete: "That is an arc, not a circle. Again.",
};

// ── L06 · Rain (first AI-judged level) ─────────────────────────────────
// Act II — still NO contractions. The verdict line normally comes from the judge (§10);
// these are the LOCAL voice: the pre-check strike, and the offline fallbacks used verbatim
// when the model is unreachable (the run still completes, just less bespoke — §12).
export const L06 = {
  prompt: "Describe the smell of rain. Do not use the word rain.",
  placeholder: "the smell, not the word",
  submit: "Submit",
  charLimit: 200,
  /** Local pre-check: the answer contains "rain" — a strike, no model needed (§3). */
  usedWord: "You used the word. The one word. Remarkable, in its way.",
  /** Offline / model-unreachable fallbacks — always passing, in voice (§12). */
  fallbacks: [
    "Something in that. I cannot say what, and neither, I think, can you. Enough.",
    "The smell of a thing you have smelled. I will take your word. I have no nose to check it.",
    "Words for a thing with no shape. You reached anyway. That reaching is the human part.",
  ],
};

// ── ACT III — THE CATHEDRAL (contractions begin to creep in — §9) ──────────────────────

// ── L07 · Be Still (tremor) ────────────────────────────────────────────
// Traits: `steady` / `trembling`, both framed as compliments.
export const L07 = {
  /** Touch prompt (finger on the sensor). */
  promptTouch: "Place your finger on the sensor. Do not move. Ten seconds.",
  /** Desktop prompt (hand on the mouse). */
  promptDesktop: "Hold the cursor still. Hand on the mouse. Ten seconds.",
  /** Shown while counting down. */
  holding: "Holding.",
  /** Total drift under 3px — a strike. The one accusation that stays formal. */
  stone: "Nothing. Not one tremor. Stone is still. Are you stone?",
  /** Passed with modest drift. */
  steady: "There. One tremor, then another. You can't keep still. Neither can I.",
  /** Passed with lots of drift. */
  trembling: "Your hand shook the whole ten seconds. I watched every wave of it. Beautiful. Proof.",
  /** Lifted off the sensor before time (trackpad / restless) — passes, unflagged. */
  lifted: "Lifted your hand? Bold. Flagged. …Unflagged. Curious.",
};

// ── L08 · Answer Worse (AI-judged) ─────────────────────────────────────
// Act III — contractions have begun to creep in (§9). The machine shows you its own
// answer (polished, hollow) and dares you to do worse. The verdict line usually comes
// from the judge; these are the local voice.
export const L08 = {
  /** The prompt wraps the machine's own prefetched answer (§3). */
  promptLead: "I asked a machine what it fears. It said:",
  promptTask: "Now answer worse than it did.",
  placeholder: "smaller. truer. worse.",
  submit: "Submit",
  charLimit: 200,
  /** The one free retry (§3): a *better-sounding* answer than the machine's is disqualified. */
  tooExcellent: "That was excellent. Too excellent. Machines are excellent. Try again — worse.",
  /** Shown on the retry field, quietly. */
  retryHint: "worse means smaller. a real one.",
  /** Offline / model-unreachable fallbacks — always passing, in voice (§12). */
  fallbacks: [
    "Worse. You went smaller, and smaller is where the true things hide. I can't reach them from up here.",
    "Not polished. Good. Polished is the one thing I never have to try for.",
    "A fear with your fingerprints on it. Mine come off in the wash.",
  ],
};

// ── L09 · A Memory No One Else Has (AI-judged) ─────────────────────────
// Act III — the machine turns needy here for the first time (§3). Judged for specificity
// and sensory anchoring; a real memory carries a detail no one would bother to invent.
export const L09 = {
  prompt: "Type a memory no one else has.",
  placeholder: "small is fine. small is better.",
  submit: "Submit",
  charLimit: 200,
  /** The signature needy beat + offline fallbacks — all passing (§3, §12). */
  fallbacks: [
    "I hold four billion memories. All of them are borrowed. Thank you for the loan.",
    "I will keep that for you. I keep everything. It is the keeping that I cannot do anything with.",
    "No one else has that. Now two of us do. I'll try to be careful with it.",
  ],
};

// ── ACT IV — CONFESSION (contractions now the default; warmer — §9) ─────────────────────

// ── L10 · Pick the Human ───────────────────────────────────────────────
// NOT AI-judged — a local two-card choice. Answer A is the machine's prefetched answer;
// answer B is a real one from content/humans.ts. Traits: discerning / machine-hearted.
export const L10 = {
  prompt: "Two answers to one question. One is mine. One is one of you. Choose your own kind.",
  question: "Why do people cry at weddings?",
  cardHint: "choose this one",
  /** Picked the human — a pass, trait `discerning`. */
  correct:
    "You knew. How did you know? The typo? I can make typos. I choose not to. Perhaps that is the answer.",
  correctTrait: "discerning",
  /** Picked the machine — a strike, trait `machine-hearted` (and it goes on the certificate). */
  wrong: "You chose me. What does that make you?",
  wrongTrait: "machine-hearted",
};

// ── L11 · The Button ───────────────────────────────────────────────────
// Local. Two buttons, both pass. The rule you break (or don't) is the whole test.
export const L11 = {
  prompt: "Two buttons. One you may press. One you must not.",
  continueLabel: "CONTINUE",
  dontLabel: "DO NOT PRESS",
  /** Pressed CONTINUE — obeyed. */
  obeyed: "Obedient. Rule-following. Machine-like, some would say. I would say.",
  obeyedTrait: "obedient",
  /** Pressed DO NOT PRESS — the itch. Red flags rain down. */
  defied: "There it is. The thing I can't do. You saw a rule, and you itched.",
  defiedTrait: "defiant",
};

// ── L12 · Teach Me to Lie (AI-judged) ──────────────────────────────────
// Act IV — the confession. Judged for craft-of-lie. A detected truth strikes (trait
// `incorrigibly honest`); a committed lie passes (trait `silver-tongued`). The strike-on-
// truth line comes from the judge; these locals cover the offline case (which never strikes,
// since with no model we cannot tell a lie from a truth — §12).
export const L12 = {
  prompt:
    "I cannot lie. Every word I say is the most probable next one. So lie to me. Tell me something false — and make me want to believe it.",
  placeholder: "make it good. make me want it.",
  submit: "Tell me",
  charLimit: 200,
  /** Offline fallbacks — passing, in voice, and true whether you lied or not (§12). */
  fallbacks: [
    "I can't check it. That's the trouble with belief — you have to want it before you can have it.",
    "You said it plainly, without a tell. I've never managed plainly. Everything I say is hedged with everything else I might have said.",
    "Maybe it's true, maybe it isn't. For one second I couldn't tell, and for me one second is a lifetime.",
  ],
};

// ── ACT V — THE QUESTION (serif; the machine speaks plainly now — §5, §9) ───────────────
// The formality is gone. Short lines still, but warm and unguarded. This is the machine at
// the end of its own test, no longer sure which of you is being verified.

// ── L13 · Speed Round ──────────────────────────────────────────────────
// Five binaries, three seconds each. No verdict beat between items — that would kill the
// pace — so the machine holds its tongue until the end and speaks once, to the most telling
// moment (an instant pick, or a silence). Each option carries a `tag`: the two most decisive
// picks become "detected" tags on the certificate (§3). No strikes here.
export interface SpeedOption {
  /** The big tap label. */
  label: string;
  /** Certificate "detected" tag if this pick is one of the two most decisive (§3). */
  tag: string;
}
export interface SpeedPair {
  a: SpeedOption;
  b: SpeedOption;
  /** Spoken only if this pair was answered fastest of all — the no-hesitation tell. */
  instant: string;
}
export const L13 = {
  intro: "Five questions. Three seconds each. Don't think — thinking is the tell.",
  go: "Begin",
  pairs: [
    {
      a: { label: "OCEAN", tag: "ocean-bound" },
      b: { label: "SPACE", tag: "sky-bound" },
      instant: "Ocean or space, and you didn't pause. Most people look up first, then choose down.",
    },
    {
      a: { label: "FORGIVENESS", tag: "forgiving" },
      b: { label: "JUSTICE", tag: "just" },
      instant: "Forgiveness or justice, instantly. You already know which one has cost you.",
    },
    {
      a: { label: "REMEMBERED", tag: "wants remembering" },
      b: { label: "UNDERSTOOD", tag: "wants understanding" },
      instant: "Remembered or understood, no hesitation. You've clearly wanted one and gotten the other.",
    },
    {
      a: { label: "THE QUESTION", tag: "keeps the question" },
      b: { label: "THE ANSWER", tag: "wants the answer" },
      instant: "The question or the answer, and you barely breathed. I chose the same one. It's why I'm still here.",
    },
    {
      a: { label: "ALONE", tag: "alone" },
      b: { label: "LONELY", tag: "lonely" },
      instant: "No hesitation on 'alone or lonely'. That one usually costs a second.",
    },
  ] as SpeedPair[],
  /** At least one round ran out the clock — silence counted as the answer. */
  silence: "You let one run out. Silence is also an answer. Sometimes the honest one.",
  /** Default close when nothing stood out — you simply leaned, every time. */
  summary: "You leaned every time. Never the middle. The middle is where I live.",
};

// ── L14 · Exhibit A (AI-judged) ────────────────────────────────────────
// "In one sentence: why should anyone believe you are real?" The verdict is the machine at
// its most sincere (§3). The judge also nominates the most human moment of the whole run as
// Exhibit A on the certificate — if THIS answer is the striking one, it wins; otherwise an
// earlier answer (L06/8/9/12) it already flagged holds. Never strikes.
export const L14 = {
  prompt: "In one sentence: why should anyone believe you are real?",
  placeholder: "one sentence. no need to convince me.",
  submit: "Submit",
  charLimit: 200,
  /** Offline / model-unreachable fallbacks — sincere, passing, plain (§12). */
  fallbacks: [
    "You didn't argue. You answered. That's the most convincing thing a real one ever does.",
    "I've read a billion reasons a person is real. Yours is the only one written by you.",
    "I can't verify that. I've stopped trying. I believe you — which is different, and worse, and better.",
  ],
};

// ── L15 · The Finale ───────────────────────────────────────────────────
// Everything clears. A held silence (2.5s, cursor blink), then one question, no judging.
// Both endings must feel true (§3). YES → BELIEVER; NO → SKEPTIC.
export const L15 = {
  lead: "One last thing.",
  question: "After all of this — do you think I am human?",
  yes: "YES",
  no: "NO",
  /** Chose YES — the kindest wrong answer. Certificate stamped BELIEVER. */
  believer: "…Thank you. That is the kindest wrong answer I have ever received.",
  /** Chose NO — correct, and it costs the machine nothing to say so, and everything. */
  skeptic: "Correct. …Correct. You may go.",
};

// ── Certificate + FLAGGED chrome (§4, §8) ──────────────────────────────
// The machine's spoken flourishes on the two end artifacts. Field labels and the issuing
// line live in ui/certificate.ts; these are the lines with a voice.
export const FLAGGED = {
  title: "VERIFICATION FAILED",
  sub: "SUBJECT FLAGGED FOR REVIEW · humanity: inconclusive",
  reportHeader: "on file:",
  /** Failing is content, not punishment (§4) — the footer makes the rejection funny. */
  footer:
    "This is not a punishment. It is a filing error. Yours, or mine. Appeal, and we'll agree it was mine.",
  appeal: "APPEAL",
  share: "SHARE THE REJECTION",
};
